import { Link } from "react-router-dom";
import { useUserContext } from "../../context/UserContext";
import { track } from "../../utils/analytics";
import { isFreeTier } from "../../utils/userUtils";

/**
 * Signed-in free accounts get the logged-out deals pages (as pinto does). Their
 * "sign up" moments become "upgrade": the paywall at /setup, the same plan →
 * stores → checkout flow pinto opens as /paywall?mode=upgrade.
 */
export const FREE_UPGRADE_PATH = "/setup";

export function freeUpgradeUrl(source: string): string {
  return `${FREE_UPGRADE_PATH}?upgradeSource=${encodeURIComponent(source)}`;
}

export function trackFreeUpgrade(source: string, retailer = "local") {
  track("free_upgrade_started", { surface: "web", retailer, trigger: source, source: "home_feed" });
}

/** Who is looking at the public feed: a guest, or a signed-in free member. */
export function useHomeFeedViewer() {
  const { user } = useUserContext();
  const freeMember = Boolean(user) && isFreeTier(user);
  return {
    user,
    freeMember,
    /** Where a sign-up/unlock CTA goes: get-started for guests, the upgrade flow for free members. */
    gateUrl: (guestUrl: string, source: string) => (freeMember ? freeUpgradeUrl(source) : guestUrl),
    /** The account ZIP, used when the browser has none saved. */
    accountZip: freeMember && user?.zipCode && /^\d{5}$/.test(user.zipCode) ? user.zipCode : "",
  };
}

function accountLabel(user: { userId?: string } | null): string {
  return user?.userId?.includes("@") ? user.userId : "Account";
}

function PersonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

/**
 * Right side of the public header for a signed-in free member: a gold Upgrade button
 * and a compact account button to Settings (person icon; the email too on desktop).
 */
export function FreeMemberHeaderActions({
  source,
  retailer,
  ink = "#170B26",
  gold = "#FFD24A",
}: {
  source: string;
  retailer?: string;
  ink?: string;
  gold?: string;
}) {
  const { user } = useUserContext();
  const text = accountLabel(user);
  return (
    <>
      <Link
        to={freeUpgradeUrl(`${source}_header`)}
        onClick={() => trackFreeUpgrade(`${source}_header`, retailer)}
        className="flex h-9 flex-shrink-0 items-center whitespace-nowrap rounded-full px-4 text-sm font-extrabold no-underline hover:brightness-95 sm:h-10 sm:px-5"
        style={{ background: gold, color: ink }}
        data-testid="header-upgrade"
      >
        Upgrade
      </Link>
      <Link
        to="/settings"
        aria-label={`Account settings (${text})`}
        title={text}
        className="flex h-9 flex-shrink-0 items-center gap-2 rounded-full border border-white/20 pl-1 pr-1 text-[13px] font-semibold text-white no-underline transition-colors hover:bg-white/10 sm:h-10 lg:pr-3"
        data-testid="header-account"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 sm:h-8 sm:w-8" aria-hidden="true">
          <PersonIcon />
        </span>
        <span className="hidden max-w-[180px] truncate lg:inline">{text}</span>
      </Link>
    </>
  );
}
